/* SHARED BS COMPUTE ENGINE */
// ★ A1 — the runtime BS rows (fundraising rounds, implementation-deferred revenue) are
// spliced by THE ONE injector, which BalanceSheetPage calls too. This engine used to carry
// its own copy of that splice; the page carried a second one that ordered rounds the other
// way round. See engines/dynamicLines.js.
import { injectDynamicBSLines } from './dynamicLines';

const r2 = n => Math.round(n * 100) / 100;
// ★ SWEEP B2-ii: the hardcoded 2024-2026 ALL_M window is DELETED. It was Semitek's
// model window used as a back-compat default; every caller passes allMonths
// (runFullModel always does) and no page imports the export — verified. An
// absent window now yields an empty one (honest-empty), not a foreign calendar.
const prevMonth = (months, m) => { const i = months.indexOf(m); return i > 0 ? months[i-1] : null; };

// ★ SWEEP B4b (A8): the hardcoded SEMITEK chart of accounts that used to live here
// is DELETED. Its only consumers were the two `|| BS_LINES` fallbacks (this engine
// and BalanceSheetPage), both now honest-null.


// Phase 3b legacy fallback: when bsLines is the hardcoded BS_LINES (no
// module_link_ref fields), this map keeps the old id-based wiring alive.
// Dynamic bsLines from buildBSLinesForCompany carry module_link_ref directly
// on each leaf, so this map is only consulted for legacy callers.
const LEGACY_MODULE_LINK_REF = {
  prepaid_expenses:           'prepaid_balance',
  deferred_rev_stripe:        'defrev_stripe',
  deferred_rev_chargebee:     'defrev_chargebee',
  deferred_revenue:           'defrev_pa',
  deferred_rev_ae_quota:      'defrev_ae_quota',
  deferred_rev_company_quota: 'defrev_company_quota',
};

/* W-073 (a), 2026-09-30: THE ONE RULE for "which balance-sheet lines does this engine forecast from a bs_assumptions row".
 * Both predicates are used by computeBS below AND by the pre-flight's P17, so the check cannot disagree with the engine:
 *   isBsValueLeaf         -- a line this loop VALUES (headers, totals and the check row are skipped);
 *   isOverlayFilledBSLine -- a runtime row the loop fills from the forecast overlay BEFORE it reads any assumption
 *                            (fundraising rounds fr_*, implementation deferred revenue deferred_rev_impl_*). It never
 *                            needs a row.
 * Every other value leaf reads assumptions[line.id]; with no row it forecasts as 0, silently (the loop's
 * `if (!assum)` arm). P17 names that state. */
export const isBsValueLeaf = line => !(line.type === "header" || line.sumOf || line.type === "check");
export const isOverlayFilledBSLine = id => id.startsWith('fr_') || id.startsWith('deferred_rev_impl_');

export function computeBS(cutoff, assumptions, actuals, forecast, isRef, frLines, implDeferredLines, allMonths, bsLines) {
  const MONTHS = allMonths || [];
  const A = actuals, H = forecast, IS_REF = isRef;
  const result = {};
  const lastActual = id => r2(A[id]?.[cutoff] || 0);

  // Phase 3b: accept dynamic bsLines from buildBSLinesForCompany. Legacy
  // ★★ SWEEP B4b (A8) — NO CHART OF ACCOUNTS => NO BALANCE SHEET.
  // This was `bsLines || BS_LINES`: a tenant with no bs_actuals_meta had its balance
  // sheet COMPUTED ON SEMITEK'S CHART (chase_money_market, ar_stripe, ar_chargebee,
  // deferred_discount …). Not a render fallback — real numbers on another company's
  // accounts. A chart of accounts is not derivable, so we return honest-empty and the
  // orchestrator records the gap (missingChartOfAccounts).
  // Reachable only before onboarding Step 4 writes bs_actuals_meta — the state the
  // tenant-#2 walk lives in; cid=1/2/3 all have it (49/49/41 leaves).
  if (!bsLines) return {};
  // ★ A1 — ONE injector, shared with BalanceSheetPage. Returns `bsLines` BY REFERENCE when
  // there is nothing to inject, preserving the old no-copy fast path exactly.
  const lines = injectDynamicBSLines(bsLines, frLines, implDeferredLines);

  // Phase 3b: find the rolling-RE and YTD-NI line ids by ROLE (replaces
  // hardcoded "retained_earnings" / "net_income" id lookups in the formula
  // handler below). Falls back to the hardcoded ids when bsLines doesn't
  // emit role (legacy BS_LINES path).
  const reLineId = lines.find(l => l.role === 'rolling_retained_earnings')?.id || 'retained_earnings';
  const niLineId = lines.find(l => l.role === 'ytd_net_income')?.id           || 'net_income';

  lines.forEach(line => {
    if (!isBsValueLeaf(line)) return;   // W-073 (a): the shared rule (see isBsValueLeaf)
    result[line.id] = {};
    MONTHS.forEach(m => {
      if (m <= cutoff) { result[line.id][m] = r2(A[line.id]?.[m] || 0); return; }
      // Dynamic fundraising and implementation deferred revenue lines: read from the bsForecast overlay
      // (W-073 (a): ONE predicate, shared with the pre-flight's P17 -- these never need an assumption row)
      if (isOverlayFilledBSLine(line.id)) {
        result[line.id][m] = r2(H[line.id]?.[m] || 0);
        return;
      }
      const assum = assumptions[line.id];
      if (!assum) { result[line.id][m] = 0; return; }
      const d = assum.driver, pm = prevMonth(MONTHS, m);
      const prior = pm ? (result[line.id][pm] || 0) : lastActual(line.id);
      if (d==="dso") {
        // Phase 6: base_is_line_id picks any IS_REF line as the DSO divisor.
        // Falls back to total_revenue (the pre-Phase-6 hardcoded behavior)
        // when the field is absent, preserving legacy callers.
        const baseKey = assum.base_is_line_id || 'total_revenue';
        result[line.id][m] = r2((IS_REF[baseKey]?.[m]||0)*assum.days/30);
      }
      else if (d==="dpo") {
        // Phase 6: same pattern for DPO. Default vendor_opex matches the
        // pre-Phase-6 engine behavior (NOT total_cogs_sga — the legacy
        // assum.base==='cogs_sga' label was misleading; engine actually
        // divided by vendor_opex).
        const baseKey = assum.base_is_line_id || 'vendor_opex';
        result[line.id][m] = r2((IS_REF[baseKey]?.[m]||0)*assum.days/30);
      }
      else if (d==="constant") result[line.id][m] = assum.fz ? 0 : r2(assum.constantValue !== undefined ? assum.constantValue : lastActual(line.id));
      else if (d==="formula") {
        if (assum.formula==="accum_depr") result[line.id][m] = r2(prior-(IS_REF.monthly_depreciation?.[m]||0));
        else if (assum.formula==="accum_amort") result[line.id][m] = r2(prior-(IS_REF.monthly_amortization?.[m]||0));
        else if (assum.formula==="fa_gross") result[line.id][m] = r2(prior+(IS_REF.grossAdditions?.[line.id]?.[m]||0));
        else if (assum.formula==="ia_gross") result[line.id][m] = r2(prior+(IS_REF.grossAdditions?.[line.id]?.[m]||0));
        else if (assum.formula==="retained_earnings") {
          /* ★★ THE YEAR-END CLOSE. January's retained earnings = prior-December retained
             earnings + prior-December YTD net income; every other month carries forward.
             ⛔ THE FORMULA WAS NEVER WRONG — it reproduces the operator's own workbook at
             three historical year ends to the cent (cid=8: 2020-12, 2021-12, 2022-12).
             What was wrong was WHERE IT READ THE SECOND TERM FROM.

             ⇒ IT USED TO READ `result[niLineId]?.[pd]` — the ytd_net_income LEAF'S CACHED
               OUTPUT — which is a FORWARD REFERENCE. This loop is LINES-outer/MONTHS-inner,
               and `rolling_retained_earnings` sorts at display_order 980 while
               `ytd_net_income` sorts at 990 (inferBsMeta BANDS; buildBSLines sorts by it),
               so every month of THIS line is computed before that line exists. At January
               `result[niLineId]` was `undefined`, the `|| 0` arm was taken, and retained
               earnings carried forward UNCHANGED — the year's net income was discarded.
               MEASURED on cid=8: 1,669,801.60 lost at 2024-01; 17,981,978.91 across two
               Januaries at 33 months. Equity was UNDERSTATED; there was no double count.
               Invisible on every live tenant only because 2023-12 is their LAST forecast
               month, so no January ever follows.

             ⇒ THE FIX IS TO READ THE SOURCE, NOT A SIBLING'S CACHE (Principle 0). The
               ytd_net_income leaf is DEFINED as `IS_REF.net_income_ytd[m]` five lines
               below, so `IS_REF.net_income_ytd[pd]` IS that leaf's December value — not a
               fallback for it. No forward reference remains, and this arm is now
               ORDER-INDEPENDENT by construction rather than by luck.
             ⛔ THE ACTUALS ARM IS UNTOUCHED AND STILL WINS: when the prior December is a
               closed month, `A[niLineId][pd]` is the stored GL figure and is read first.
             ⛔ THE `||` CHAIN IS DELIBERATELY LEFT AS-IS (not `??`). It is a FALSY test, so
               a genuine stored $0.00 December would fall through — filed, not changed here,
               because converting it is a separate behaviour change needing its own arm. */
          const [y] = m.split("-");
          if (m===y+"-01") {
            const pd = String(+y-1)+"-12";
            result[line.id][m]=r2((A[reLineId]?.[pd]||result[reLineId]?.[pd]||0)+(A[niLineId]?.[pd]||IS_REF.net_income_ytd?.[pd]||0));
          }
          else result[line.id][m]=r2(result[line.id][pm]||0);
        } else if (assum.formula==="net_income_ytd") result[line.id][m]=r2(IS_REF.net_income_ytd?.[m]||0);
      } else if (d==="module_link") {
        // Phase 3b: data-driven IS_REF lookup via line.module_link_ref
        // (set by buildBSLinesForCompany from bs_actuals_meta and
        // SYNTHETIC_BS_LINES.moduleGated). Falls back to id-based legacy
        // map for callers passing the hardcoded BS_LINES.
        const refKey = line.module_link_ref || LEGACY_MODULE_LINK_REF[line.id];
        if (refKey && IS_REF[refKey]) {
          result[line.id][m] = r2(IS_REF[refKey]?.[m] || 0);
        } else {
          result[line.id][m] = r2(assum.values?.[m] !== undefined ? assum.values[m] : (H[line.id]?.[m]||0));
        }
      } else if (d==="assumption") result[line.id][m]=r2(assum.values?.[m] !== undefined ? assum.values[m] : (H[line.id]?.[m]||0));
    });
  });

  lines.forEach(line => {
    if (!line.sumOf) return;
    result[line.id]={};
    MONTHS.forEach(m => {
      if (m>cutoff && (assumptions[line.id]?.driver==="assumption"||assumptions[line.id]?.driver==="module_link")) {
        const av = assumptions[line.id].values?.[m];
        result[line.id][m]=r2(av !== undefined ? av : (H[line.id]?.[m]||0));
      } else if (m>cutoff && H[line.id]?.[m]!==undefined) result[line.id][m]=r2(H[line.id][m]);
      else result[line.id][m]=r2(line.sumOf.reduce((s,c)=>s+(result[c]?.[m]||0),0));
    });
  });

  result.check={};
  MONTHS.forEach(m => {
    const diff=r2((result.total_assets?.[m]||0)-(result.total_le?.[m]||0));
    /* THE CHECK REPORTS THE TRUE IMBALANCE, AND NOTHING ABSORBS IT (W-017, 2026-09-30). A plug used to push any
       imbalance into total_current_assets / total_assets here: it had no recorded reason (it came with the initial
       commit), no downstream reader (the CFS and the sweep read BS leaves; P9 reads this check), and it made an
       unbalanced balance sheet LOOK balanced. It is deleted: every total sums its own rows, and the difference
       shows on the "Balance Check" row -- this `check` (label in lineCategories.js). */
    result.check[m]=diff;
  });

  return result;
}

export { prevMonth, r2 };
